import { useGet, usePost, postProvider, updateProvider, PostFormat, GetFormat, postCall } from 'sharedKernel/api'
import { api } from 'sharedKernel/api/config'

// Types
export interface ScoutingOpportunityFileRow {
  id?: string
  name?: string
  path?: string
  type?: string
}

export interface ScoutingOpportunity {
  id: string
  statusId: string
  title: string
  description: string
  requesterName?: string
  institutionName?: string
  operationArea?: string
  verticals?: string[]
  otherVertical?: string
  impactedBy?: string
  impactedAudience?: string
  references?: string
  supportLink?: string
  interestDeadline?: string
  maxValue?: string
  maximumOpportunityValue?: number | string
  coverImagePath?: string
  style?: {
    coverImagePath?: string
  }
  imagePath?: string
  image?: string
  files?: ScoutingOpportunityFileRow[]
  totalStartupsInteressed?: number
  interestDemonstrationsCount?: number
  demonstratedInterestCount?: number
  interestCount?: number
  createdAt?: string
  updatedAt?: string
  scouting?: ScoutingDetail[]
}

export interface ScoutingDetail {
  id: string
  title: string
  criteria: ScoutingCriterion[]
  stages: ScoutingStage[]
}

export interface ScoutingCriterion {
  id?: string
  opportunityScoutingId?: string
  scoutingCriterionId: number | string
  condition: string
  valueOne: any
  valueTwo?: any
}

export interface ScoutingStage {
  id?: string
  opportunityScoutingId?: string
  title: string
  sort: number
  scoutingCriterionId?: string
}

export interface ScoutingStatus {
  id: string
  code: string
  name: string
}

export interface ScoutingCriterionDefinition {
  id: string
  code: string
  name: string
  type: string
}

export interface ScoutingStartup {
  id: string
  name: string
  logo?: string
  description?: string
  businessModel?: string
  target?: string
  numberEmployees?: string
  addressState?: string
  selected: boolean
  [key: string]: any
}

interface EcosystemStartupResponse {
  rows: any[]
  totalRows: number
}

export const useGetOpportunities = () => {
  const request: GetFormat = {
    url: '/v1/hub/scouting/opportunity'
  }
  return useGet<{ rows: ScoutingOpportunity[]; totalRows: number; page: number; limit: number; totalPages: number }>(
    request,
    'scouting-opportunities'
  )
}

export const useGetOpportunityById = (id: string) => {
  const request: GetFormat = {
    url: `/v1/hub/scouting/opportunity/${id}`
  }
  return useGet<{ id: string; data: ScoutingOpportunity }>(
    request,
    `scouting-opportunity-${id}`,
    { enabled: !!id }
  )
}

export const useGetOpportunityStatus = () => {
  const request: GetFormat = {
    url: '/v1/hub/scouting/opportunity/status'
  }
  return useGet<{ rows: ScoutingStatus[]; totalRows: number }>(
    request,
    'scouting-opportunity-status'
  )
}

export const useGetScoutingCriteria = () => {
  const request: GetFormat = {
    url: '/v1/hub/scouting/criterion'
  }
  return useGet<{ rows: ScoutingCriterionDefinition[]; totalRows: number }>(
    request,
    'scouting-criteria'
  )
}

export const useGetScoutingCriterionById = (id: string) => {
  const request: GetFormat = {
    url: `/v1/hub/scouting/criterion/${id}`
  }
  return useGet<{ id: string; data: ScoutingCriterionDefinition }>(
    request,
    `scouting-criterion-${id}`,
    { enabled: !!id }
  )
}

export const scoutingStageStartupsQueryKeyPrefix = (opportunityScoutingId: string): string =>
  `scouting-opportunity-scouting-stage-${opportunityScoutingId}-`

export const scoutingStartupsListQueryKey = (opportunityId: string, scoutingId: string): string =>
  `scouting-startups-${opportunityId}-${scoutingId}`

export const useGetOpportunityScoutingStage = (opportunityScoutingId: string, stageId: string) => {
  const request: GetFormat = {
    url: `/v1/hub/scouting/opportunityScouting/${opportunityScoutingId}/stage/${stageId}`
  }
  return useGet<{ rows: ScoutingStartup[]; totalRows: number }>(
    request,
    `${scoutingStageStartupsQueryKeyPrefix(opportunityScoutingId)}${stageId}`,
    { enabled: !!opportunityScoutingId && !!stageId }
  )
}

export const useGetStartupsByStage = (opportunityScoutingId: string, stageId: string) =>
  useGetOpportunityScoutingStage(opportunityScoutingId, stageId)

export const useGetScoutingStartups = (opportunityId: string, scoutingId: string) => {
  const request: GetFormat = {
    url: `/v1/hub/scouting/opportunity/${opportunityId}/scouting/${scoutingId}`
  }
  return useGet<{ rows: ScoutingStartup[]; totalRows: number }>(
    request,
    scoutingStartupsListQueryKey(opportunityId, scoutingId),
    { enabled: !!opportunityId && !!scoutingId }
  )
}

export interface ConnectionFunnelStartup {
  startupCompanyId?: string
  name?: string
  logoUrl?: string
  companyTypeTag?: string
  segment?: string
  summary?: string
  businessModel?: string
  targetAudience?: string
  employeeCount?: string
  state?: string
  currentStageTitle?: string
  opportunityScoutingStartupId?: string
}

export interface ConnectionFunnelStage {
  id?: string
  title?: string
  sort?: number
  isSyntheticInterest?: boolean
  startupCount?: number
  startups?: ConnectionFunnelStartup[]
}

export interface ConnectionFunnelResponse {
  opportunityId?: string
  opportunityScoutingId?: string
  totalStartups?: number
  stages?: ConnectionFunnelStage[]
}

export const connectionFunnelQueryKey = (opportunityId: string): string =>
  `scouting-connection-funnel-${opportunityId}`

export const buildConnectionFunnelRequest = (
  opportunityId: string,
  match = ''
): PostFormat => ({
  url: `/v1/hub/scouting/opportunity/${opportunityId}/connection-funnel`,
  data: {
    filterBy: [],
    sort: '',
    limit: 1000,
    page: 1,
    totalRows: 0,
    match,
  },
})

export const useGetConnectionFunnel = (opportunityId: string, match = '') => {
  const request = buildConnectionFunnelRequest(opportunityId, match)

  return usePost<ConnectionFunnelResponse>(
    request,
    `${connectionFunnelQueryKey(opportunityId)}-${match}`,
    { enabled: !!opportunityId, retry: false }
  )
}

export const useGetEcosystemStartupsForScouting = () => {
  const request: PostFormat = {
    url: '/v1/hub/company/ecosystem/startups',
    data: {
      filterBy: [],
      sort: '',
      limit: 1000,
      page: 1,
      totalRows: 0,
      match: ''
    }
  }

  return usePost<EcosystemStartupResponse>(
    request,
    'scouting-ecosystem-startups'
  )
}

export const useSaveOpportunity = (onSuccess?: (success: any) => Promise<void>, onError?: (error: any) => Promise<void>) => {
  return postProvider(onSuccess, onError)
}

export const useChooseStartup = (onSuccess?: (success: any) => Promise<void>, onError?: (error: any) => Promise<void>) => {
  return postProvider(onSuccess, onError)
}

export const useUpdateStartupStage = (onSuccess?: (success: any) => Promise<void>, onError?: (error: any) => Promise<void>) => {
  return updateProvider(onSuccess, onError)
}

export const buildSaveOpportunityRequest = (
  opportunityData: any
): PostFormat => {
  return {
    url: '/v1/hub/scouting/opportunity',
    data: {
      data: opportunityData
    }
  }
}

export interface OpportunityUploadFilePayload {
  content: string
  mimetype: string
  name: string
  type: string
}

export const buildUploadOpportunityFileRequest = (
  opportunityId: string,
  file: OpportunityUploadFilePayload
): PostFormat => ({
  url: `/v1/hub/scouting/opportunity/${opportunityId}/file/upload`,
  data: { data: file },
})

export const uploadOpportunityFiles = async (
  opportunityId: string,
  files: OpportunityUploadFilePayload[]
): Promise<void> => {
  await Promise.all(
    files.map(file => postCall(buildUploadOpportunityFileRequest(opportunityId, file)))
  )
}

export const removeOpportunityFile = async (fileId: string): Promise<void> => {
  await api.delete(`/v1/hub/scouting/opportunity/file/${fileId}`)
}

export const removeOpportunityFiles = async (fileIds: string[]): Promise<void> => {
  const uniqueIds = [...new Set(fileIds.filter(Boolean))]

  for (const fileId of uniqueIds) {
    await removeOpportunityFile(fileId)
  }
}

export const buildChooseStartupRequest = (
  opportunityId: string,
  scoutingId: string,
  startupId: string
): PostFormat => {
  return {
    url: `/v1/hub/scouting/opportunity/${opportunityId}/scouting/${scoutingId}/startup/${startupId}/choose`,
    data: {}
  }
}

export const buildUpdateStartupStageRequest = (
  opportunityId: string,
  scoutingId: string,
  startupId: string,
  stageId: string,
  comments?: string
): PostFormat => {
  return {
    url: `/v1/hub/scouting/opportunity/${opportunityId}/scouting/${scoutingId}/startup/${startupId}/stage/${stageId}`,
    data: {
      data: {
        comments: comments || ''
      }
    }
  }
}

